import { CsvColumnConfig, CsvColumnValidation, CsvProfile } from '../types/csv.types'
import { parseCsvStream, readSampleLines } from './csvParser'
import { detectDelimiter } from '../utils/delimiterDetector'
import { buildCsvProfile, findDuplicateColumns } from './csvProfiler'
import { convertValue } from '../utils/csvValueConverter'
import Papa from 'papaparse'
import { detectFileEncoding, toDecoderLabel } from './detectEncoding'

export interface AnalyzeOptions {
    maxPreviewRows: number
    maxSampleRows: number
    delimiter?: string
    encoding?: string
    onProgress?: (payload: { processedRows: number; progress: number; stage: string }) => void
}

// Runs a full analysis pass over the file. Enforces per-column sample caps so
// memory usage stays bounded for very large files. Total row count reflects
// however many rows Papa Parse actually streamed within `maxSampleRows`.
export async function analyzeCsv(file: File, options: AnalyzeOptions): Promise<CsvProfile> {
    const encoding = options.encoding ?? toDecoderLabel(await detectFileEncoding(file).catch(() => 'unknown' as const))
    const sampleLines = await readSampleLines(file, undefined, undefined, encoding)
    const delimiter = options.delimiter ?? detectDelimiter(sampleLines)

    const header: string[] = []
    const previewRows: Record<string, string>[] = []
    const valuesByColumn = new Map<string, string[]>()
    const warnings: string[] = []
    // Papa Parse renames duplicate headers (e.g. `a` -> `a_1`), so duplicates are only visible here.
    const renamedDuplicates = new Set<string>()
    let totalRows = 0

    await parseCsvStream(file, {
        delimiter,
        encoding,
        maxRows: options.maxSampleRows,
        onChunk: (rows, meta) => {
            Object.values(meta.renamedHeaders ?? {}).forEach((name) => renamedDuplicates.add(name))
            if (header.length === 0 && meta.header.length > 0) {
                header.push(...meta.header)
                for (const name of meta.header) {
                    if (!valuesByColumn.has(name)) valuesByColumn.set(name, [])
                }
            }

            for (const row of rows) {
                totalRows++
                if (previewRows.length < options.maxPreviewRows) previewRows.push(row)
                for (const name of header) {
                    const bucket = valuesByColumn.get(name)
                    if (bucket) bucket.push(row[name] ?? '')
                }
            }

            options.onProgress?.({
                processedRows: totalRows,
                progress: Math.min(99, Math.round((totalRows / options.maxSampleRows) * 100)),
                stage: 'profiling',
            })
        },
    })

    if (header.length === 0) {
        throw new Error('Unable to detect CSV structure or the file is empty')
    }

    const duplicateColumnNames = [...new Set([...findDuplicateColumns(header), ...renamedDuplicates])]
    if (duplicateColumnNames.length > 0) {
        warnings.push(`Duplicate column names detected: ${duplicateColumnNames.join(', ')}`)
    }

    return buildCsvProfile({
        fileName: file.name,
        fileSize: file.size,
        delimiter,
        encoding,
        header,
        valuesByColumn,
        previewRows,
        totalRows,
        duplicateColumnNames,
        warnings,
    })
}

export function validateConfiguration(configs: readonly CsvColumnConfig[], sampleRows: readonly Record<string, string>[]): CsvColumnValidation[] {
    const validations: CsvColumnValidation[] = []
    for (const config of configs) {
        let invalidCount = 0
        let validCount = 0
        const invalidExamples: string[] = []
        for (const row of sampleRows) {
            const raw = row[config.name]
            const result = convertValue(raw, config)
            if (result.ok) validCount++
            else {
                invalidCount++
                if (invalidExamples.length < 5 && result.invalidSample !== undefined) {
                    invalidExamples.push(result.invalidSample)
                }
            }
        }
        validations.push({ columnName: config.name, invalidCount, invalidExamples, validCount })
    }
    return validations
}

export interface ProcessOptions {
    delimiter: string
    encoding?: string
    columnConfigs: CsvColumnConfig[]
    outputFormat: 'csv' | 'json'
    onProgress?: (payload: { processedRows: number; progress: number }) => void
}

// Re-streams the whole file, applies the user's column config to every cell,
// and produces a normalized output Blob. Rows containing an invalid cell for
// a strongly-typed column keep the raw value; consumers should call
// validateConfiguration() first if strictness is required.
function buildJsonChunk(rows: readonly Record<string, string>[], columnConfigs: readonly CsvColumnConfig[], rowsAlreadyWritten: number): { text: string; rowCount: number } {
    let jsonBuffer = ''
    let rowCount = 0
    for (const row of rows) {
        const jsonRow: Record<string, string | null> = {}
        for (const config of columnConfigs) {
            const result = convertValue(row[config.name], config)
            jsonRow[config.outputName ?? config.name] = result.ok ? result.value : (row[config.name] ?? null)
        }
        const serialized = JSON.stringify(jsonRow)
        jsonBuffer += rowsAlreadyWritten + rowCount === 0 ? serialized : ',' + serialized
        rowCount++
    }
    return { text: jsonBuffer, rowCount }
}

function buildCsvChunk(rows: readonly Record<string, string>[], columnConfigs: readonly CsvColumnConfig[]): { text: string; rowCount: number } {
    const chunkRows: (string | null)[][] = []
    for (const row of rows) {
        const outRow: (string | null)[] = []
        for (const config of columnConfigs) {
            const result = convertValue(row[config.name], config)
            outRow.push(result.ok ? result.value : (row[config.name] ?? null))
        }
        chunkRows.push(outRow)
    }
    if (chunkRows.length === 0) return { text: '', rowCount: 0 }
    return { text: Papa.unparse(chunkRows, { delimiter: ',' }) + '\n', rowCount: chunkRows.length }
}

export async function processCsv(file: File, options: ProcessOptions): Promise<{ blob: Blob; totalRows: number }> {
    const columnConfigs = options.columnConfigs
    const header = columnConfigs.map((c) => c.outputName ?? c.name)
    const isJson = options.outputFormat === 'json'
    const encoder = new TextEncoder()
    let totalRows = 0
    let processedBytes = 0
    const estimatedBytes = file.size || 1

    // Stream output through a ReadableStream and finalize via Response.blob(),
    // which lets the browser spill large outputs to disk instead of pinning
    // them in the worker's JS heap. This is what keeps peak memory bounded
    // for very large files (hundreds of MB+).
    let controller!: ReadableStreamDefaultController<Uint8Array>
    const stream = new ReadableStream<Uint8Array>({
        start(c) {
            controller = c
        },
    })
    const blobPromise = new Response(stream).blob()

    const write = (chunk: string) => {
        if (chunk.length > 0) controller.enqueue(encoder.encode(chunk))
    }

    write(isJson ? '[' : Papa.unparse([header], { delimiter: ',' }) + '\n')

    try {
        await parseCsvStream(file, {
            delimiter: options.delimiter,
            encoding: options.encoding,
            onChunk: (rows) => {
                const { text, rowCount } = isJson ? buildJsonChunk(rows, columnConfigs, totalRows) : buildCsvChunk(rows, columnConfigs)
                totalRows += rowCount
                write(text)
                processedBytes += text.length
                options.onProgress?.({
                    processedRows: totalRows,
                    progress: Math.min(99, Math.round((processedBytes / estimatedBytes) * 100)),
                })
            },
        })

        if (isJson) write(']')
        controller.close()
    } catch (err) {
        controller.error(err)
        throw err
    }

    const rawBlob = await blobPromise
    const blob = new Blob([rawBlob], { type: isJson ? 'application/json' : 'text/csv' })
    return { blob, totalRows }
}
